import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Image, Video, ArrowLeft, ExternalLink, Eye, EyeOff, Upload, X, CloudUpload, Link2, Loader2, CheckCircle, FileVideo, FileImage } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const SECTIONS = [
  { value: 'hero', label: 'Hero Background', description: 'Main background video/image on the hero section' },
  { value: 'promo', label: 'Promotional Banner', description: 'Featured banners below categories section' },
  { value: 'featured', label: 'Featured Section', description: 'Media for featured events area' },
];

const MEDIA_TYPES = [
  { value: 'video', label: 'Video', icon: Video },
  { value: 'image', label: 'Image', icon: Image },
];

// Drag & Drop Upload Component
function MediaUploader({ onUploadComplete, onCancel }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/admin/upload-media`, formData, {
        
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        }
      });

      setUploadedFile({
        url: response.data.url,
        filename: response.data.filename,
        media_type: response.data.media_type,
        size: response.data.size
      });
      
      toast.success('File uploaded successfully!');
      onUploadComplete(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
      setUploading(false);
    }
  }, [onUploadComplete]);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'video/*': ['.mp4', '.mov', '.webm']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: false,
    disabled: uploading || uploadedFile
  });

  if (uploadedFile) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
          <CheckCircle className="w-8 h-8 text-green-500" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-green-600">Upload complete!</p>
            <p className="text-sm text-muted-foreground truncate">{uploadedFile.filename}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { setUploadedFile(null); setProgress(0); setUploading(false); }}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Preview */}
        <div className="rounded-xl overflow-hidden bg-muted aspect-video">
          {uploadedFile.media_type === 'video' ? (
            <video src={uploadedFile.url} className="w-full h-full object-contain" controls />
          ) : (
            <img src={uploadedFile.url} alt="Uploaded" className="w-full h-full object-contain" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
          ${isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}
          ${isDragAccept ? 'border-green-500 bg-green-500/5' : ''}
          ${isDragReject ? 'border-red-500 bg-red-500/5' : ''}
          ${uploading ? 'pointer-events-none opacity-70' : ''}
        `}
        data-testid="media-dropzone"
      >
        <input {...getInputProps()} data-testid="media-file-input" />
        
        {uploading ? (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-primary mx-auto animate-spin" />
            <div>
              <p className="font-medium">Uploading...</p>
              <p className="text-sm text-muted-foreground">{progress}% complete</p>
            </div>
            <Progress value={progress} className="w-full max-w-xs mx-auto" />
          </div>
        ) : (
          <>
            <CloudUpload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} />
            <p className="font-medium mb-2">
              {isDragActive ? 'Drop your file here' : 'Drag & drop your media file here'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse
            </p>
            <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <FileImage className="w-4 h-4" />
                <span>JPG, PNG, GIF, WebP</span>
              </div>
              <div className="flex items-center gap-1">
                <FileVideo className="w-4 h-4" />
                <span>MP4, MOV, WebM</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Max file size: 50MB</p>
          </>
        )}
      </div>
      
      {onCancel && (
        <Button variant="ghost" className="w-full" onClick={onCancel}>
          Cancel
        </Button>
      )}
    </div>
  );
}

export default function MediaManagement() {
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, media: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, media: null });
  const [uploadMethod, setUploadMethod] = useState('upload'); // 'upload' or 'url'
  const [uploadedMedia, setUploadedMedia] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    media_type: 'video',
    url: '',
    section: 'hero',
    link_url: '',
    is_active: true,
    display_order: 0
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/landing-media`);
      setMediaList(response.data);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      media_type: 'video',
      url: '',
      section: 'hero',
      link_url: '',
      is_active: true,
      display_order: 0
    });
    setUploadedMedia(null);
    setUploadMethod('upload');
  };

  const handleUploadComplete = (data) => {
    setUploadedMedia(data);
    setFormData(prev => ({
      ...prev,
      url: data.url,
      media_type: data.media_type
    }));
  };

  const handleCreate = async () => {
    if (!formData.title) {
      toast.error('Please enter a title');
      return;
    }
    if (!formData.url) {
      toast.error('Please upload a file or enter a URL');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/admin/landing-media`, formData);
      toast.success('Media created successfully');
      setCreateDialog(false);
      resetForm();
      fetchMedia();
    } catch (error) {
      console.error('Error creating media:', error);
      toast.error(error.response?.data?.detail || 'Failed to create media');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/landing-media/${editDialog.media.media_id}`,
        {
          title: formData.title,
          url: formData.url,
          link_url: formData.link_url || null,
          is_active: formData.is_active,
          display_order: formData.display_order
        },
        
      );
      toast.success('Media updated successfully');
      setEditDialog({ open: false, media: null });
      resetForm();
      fetchMedia();
    } catch (error) {
      console.error('Error updating media:', error);
      toast.error(error.response?.data?.detail || 'Failed to update media');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(
        `${API_URL}/api/admin/landing-media/${deleteDialog.media.media_id}`,
        
      );
      toast.success('Media deleted successfully');
      setDeleteDialog({ open: false, media: null });
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete media');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (media) => {
    setFormData({
      title: media.title,
      media_type: media.media_type,
      url: media.url,
      section: media.section,
      link_url: media.link_url || '',
      is_active: media.is_active,
      display_order: media.display_order
    });
    setEditDialog({ open: true, media });
  };

  const toggleActive = async (media) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/landing-media/${media.media_id}`,
        { is_active: !media.is_active },
        
      );
      toast.success(`Media ${!media.is_active ? 'activated' : 'deactivated'}`);
      fetchMedia();
    } catch (error) {
      console.error('Error toggling active:', error);
      toast.error('Failed to update media');
    }
  };

  const getSectionLabel = (section) => {
    return SECTIONS.find(s => s.value === section)?.label || section;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold">Media Management</h1>
            <p className="text-muted-foreground">Upload and manage videos and images for the landing page</p>
          </div>
          <Button onClick={() => setCreateDialog(true)} data-testid="add-media-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Media
          </Button>
        </div>

        {/* Media List */}
        <Card>
          <CardHeader>
            <CardTitle>Landing Page Media ({mediaList.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 skeleton rounded-lg" />
                ))}
              </div>
            ) : mediaList.length > 0 ? (
              <div className="space-y-4">
                {mediaList.map((media) => (
                  <div
                    key={media.media_id}
                    className={`flex items-center gap-4 p-4 rounded-xl border ${
                      media.is_active ? 'border-border' : 'border-border/50 opacity-60'
                    }`}
                    data-testid={`media-item-${media.media_id}`}
                  >
                    {/* Preview */}
                    <div className="w-32 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0 relative">
                      {media.media_type === 'video' ? (
                        <video
                          src={media.url}
                          className="w-full h-full object-cover"
                          muted
                        />
                      ) : (
                        <img
                          src={media.url}
                          alt={media.title}
                          className="w-full h-full object-cover"
                        />
                      )}
                      <div className="absolute top-1 left-1">
                        {media.media_type === 'video' ? (
                          <Video className="w-4 h-4 text-white drop-shadow-lg" />
                        ) : (
                          <Image className="w-4 h-4 text-white drop-shadow-lg" />
                        )}
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold truncate">{media.title}</h4>
                        {!media.is_active && (
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        <span className="capitalize">{media.media_type}</span>
                        <span>•</span>
                        <span>{getSectionLabel(media.section)}</span>
                        <span>•</span>
                        <span>Order: {media.display_order}</span>
                      </div>
                      <div className="mt-1">
                        <a
                          href={media.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1 truncate max-w-md"
                        >
                          {media.url.substring(0, 60)}...
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        </a>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleActive(media)}
                        data-testid={`toggle-active-${media.media_id}`}
                      >
                        {media.is_active ? (
                          <Eye className="w-4 h-4 text-green-500" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-muted-foreground" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(media)}
                        data-testid={`edit-media-${media.media_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => setDeleteDialog({ open: true, media })}
                        data-testid={`delete-media-${media.media_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No media yet</h3>
                <p className="text-muted-foreground mb-4">
                  Upload videos and images to customize your landing page.
                </p>
                <Button onClick={() => setCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Media
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={createDialog} onOpenChange={(open) => { if (!open) resetForm(); setCreateDialog(open); }}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>Add New Media</DialogTitle>
              <DialogDescription>
                Upload a video or image to display on the landing page.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Upload Method Tabs */}
              <Tabs value={uploadMethod} onValueChange={setUploadMethod}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload" className="flex items-center gap-2" data-testid="upload-tab">
                    <Upload className="w-4 h-4" />
                    Upload File
                  </TabsTrigger>
                  <TabsTrigger value="url" className="flex items-center gap-2" data-testid="url-tab">
                    <Link2 className="w-4 h-4" />
                    Enter URL
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="upload" className="mt-4">
                  <MediaUploader 
                    onUploadComplete={handleUploadComplete}
                    onCancel={null}
                  />
                </TabsContent>
                
                <TabsContent value="url" className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Media Type</Label>
                      <Select
                        value={formData.media_type}
                        onValueChange={(value) => setFormData({ ...formData, media_type: value })}
                      >
                        <SelectTrigger data-testid="media-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MEDIA_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center gap-2">
                                <type.icon className="w-4 h-4" />
                                {type.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="url">Media URL *</Label>
                    <Input
                      id="url"
                      placeholder="https://example.com/video.mp4"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      data-testid="media-url-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter the direct URL to your video or image file.
                    </p>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Common Fields - Always show for URL tab or after upload */}
              {(uploadedMedia || uploadMethod === 'url') && (
                <div className="space-y-4 pt-4 border-t">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      placeholder="e.g., Hero Background Video"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      data-testid="media-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Section</Label>
                    <Select
                      value={formData.section}
                      onValueChange={(value) => setFormData({ ...formData, section: value })}
                    >
                      <SelectTrigger data-testid="media-section-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SECTIONS.map((section) => (
                          <SelectItem key={section.value} value={section.value}>
                            <div>
                              <div>{section.label}</div>
                              <div className="text-xs text-muted-foreground">{section.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.section === 'promo' && (
                    <div className="space-y-2">
                      <Label htmlFor="link_url">Link URL (Optional)</Label>
                      <Input
                        id="link_url"
                        placeholder="https://example.com/promo-page"
                        value={formData.link_url}
                        onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                        data-testid="media-link-url-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Where should the banner link to when clicked?
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="display_order">Display Order</Label>
                      <Input
                        id="display_order"
                        type="number"
                        min="0"
                        value={formData.display_order}
                        onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                        data-testid="media-order-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Active</Label>
                      <div className="flex items-center gap-2 pt-2">
                        <Switch
                          checked={formData.is_active}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                          data-testid="media-active-switch"
                        />
                        <span className="text-sm text-muted-foreground">
                          {formData.is_active ? 'Visible' : 'Hidden'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCreateDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={submitting || !formData.title || !formData.url} 
                data-testid="create-media-btn"
              >
                {submitting ? 'Creating...' : 'Create Media'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ ...editDialog, open: false })}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Media</DialogTitle>
              <DialogDescription>
                Update the media details.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-title">Title</Label>
                <Input
                  id="edit-title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="edit-media-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-url">Media URL</Label>
                <Input
                  id="edit-url"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  data-testid="edit-media-url-input"
                />
              </div>

              {formData.section === 'promo' && (
                <div className="space-y-2">
                  <Label htmlFor="edit-link_url">Link URL (Optional)</Label>
                  <Input
                    id="edit-link_url"
                    placeholder="https://example.com/promo-page"
                    value={formData.link_url}
                    onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                    data-testid="edit-media-link-url-input"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-display_order">Display Order</Label>
                  <Input
                    id="edit-display_order"
                    type="number"
                    min="0"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    data-testid="edit-media-order-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Active</Label>
                  <div className="flex items-center gap-2 pt-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      data-testid="edit-media-active-switch"
                    />
                    <span className="text-sm text-muted-foreground">
                      {formData.is_active ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditDialog({ open: false, media: null }); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={submitting} data-testid="update-media-btn">
                {submitting ? 'Updating...' : 'Update Media'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ ...deleteDialog, open: false })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Delete Media
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{deleteDialog.media?.title}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, media: null })}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={submitting} data-testid="confirm-delete-media-btn">
                {submitting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
